import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldDebateRerunChoiceDialog } from "@/components/analyze/FieldDebateRerunChoiceDialog";
import { useFieldDebateRefreshLanded } from "@/hooks/useFieldDebateRefreshLanded";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod/v4";
import { usePageView } from "@/hooks/usePageView";
import { trackEvent } from "@/lib/engagement";
import { AnalyzePaperList } from "@/components/analyze/AnalyzePaperList";
import { CampScopeBanner } from "@/components/analyze/CampScopeBanner";
import {
  encodeScopeForUrl,
  decodeScopeFromUrl,
} from "@/lib/paperIdScopeUrl";
import { PaperDetail } from "@/components/expand/PaperDetail";
import { ChatToolPanel } from "@/components/analyze/ChatToolPanel";
import { ComparisonTable } from "@/components/analyze/ComparisonTable";
import { AddAngleSheet } from "@/components/angles/AddAngleSheet";
import { useAngles } from "@/hooks/useFacets";
import { GapAnalysisPanel } from "@/components/expand/GapAnalysisPanel";
import { OperationBar } from "@/components/expand/operation-bar";
import { ActiveFiltersBadge } from "@/components/analyze/ActiveFiltersBadge";
import { useConceptPaper } from "@/hooks/usePapers";
import { useNotes } from "@/hooks/useNotes";
import { useWorkingSetActions, useWorkingSets } from "@/hooks/useWorkingSets";
import type { PaperFilters } from "@/hooks/useFilteredPapers";
import { useGapAnalysisStore } from "@/stores/gap-analysis-store";
import { useGapAnalysisGenerationStore } from "@/stores/gap-analysis-generation-store";
import { useGapAnalysisGenerate } from "@/hooks/useGapAnalysisGenerate";
import { useGapFillStore } from "@/stores/gap-fill-store";
import { useGapFillGenerate } from "@/hooks/useGapFillGenerate";
import { GapFillModal } from "@/components/analyze/GapFillModal";
import { computeFingerprint } from "@/lib/resultFingerprint";
import type { Gap } from "@/types/audit";
import { useFieldDebateGenerate } from "@/hooks/useFieldDebateGenerate";
import { useScoring } from "@/hooks/useScoring";
import { usePdfRetrieval } from "@/hooks/usePdfRetrieval";
import { useMonitorRun } from "@/hooks/useMonitor";
import { useFieldDebateQuota } from "@/hooks/useFieldDebateQuota";
import {
  rebuildConfirmMessage,
  rebuildAtLimit,
  REBUILD_LIMIT_TITLE,
} from "@/lib/fieldDebateQuota";
import { useFieldDebateGenerationStore } from "@/stores/field-debate-generation-store";
import { FieldDebateView } from "@/components/analyze/FieldDebateView";
import { useDebateStore } from "@/stores/debate-store";
import { useFilterStore } from "@/stores/filter-store";
import { useComparisonStore } from "@/stores/comparison-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BarChart3, Copy, FileDown, Link2, Search, Trash2, RefreshCw, Sparkles, Swords } from "lucide-react";
import {
  useAnalyzeToolState,
  outOfScopePapers,
  debateComparisonPaperIds,
  debateRefreshDiff,
  debateScopeChanged,
  DRIFT_ADVISORY_PCT,
} from "@/hooks/useAnalyzeToolState";
import {
  computeApplyFieldDebateScope,
  type ToolStatusResult,
} from "@/lib/analyzeToolState";
import { AnalyzeOverview, type ArtifactCard } from "@/components/analyze/AnalyzeOverview";
import { TOOL_META } from "@/components/analyze/toolMeta";
import { AnalyzeScopeStrip } from "@/components/analyze/AnalyzeScopeStrip";
import { AnalyzeActivePanel, type ActiveTool } from "@/components/analyze/AnalyzeActivePanel";
// useGuideState no longer needed on analyze.tsx — one-shot triggers retired
// 2026-05-21; persistedPhase below is the single phase signal for this route.
import { usePersistedGuidePhase } from "@/hooks/usePersistedGuidePhase";
import { shouldHideFromRail } from "@/lib/analyzePipelineState";
import { useAnalyzeRecs } from "@/hooks/useAnalyzeRecs";
import { exportPapersToCSV } from "@/lib/csv-export";
import apiClient from "@/lib/api-client";
import { toast } from "@/lib/toast";
import { useChatStore } from "@/stores/chat-store";
import { guideSignals } from "@/lib/guideSignals";
import { useThreadStore } from "@/stores/thread-store";
import { useTags } from "@/hooks/useTags";
import { useSpotlight } from "@/hooks/useSpotlight";
import { useConcept } from "@/hooks/useConcepts";
import { LandscapeOverlay } from "@/components/landscape/LandscapeOverlay";
import { useLandscapeStore } from "@/stores/useLandscapeStore";
import type { PaperWithRole } from "@/types/paper";
import type { WorkflowRecommendation } from "@/lib/workflowAdvisor";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { UpgradeCard } from "@/components/UpgradeCard";

const analyzeSearchSchema = z
  .object({
    scope: z
      .string()
      .regex(/^(?:camp|bridges|tension):[\w-]+(?:__[\w-]+)?$/)
      .optional(),
    activeTool: z.enum(["debate", "audit", "comparison", "chat"]).optional(),
    autorun: z.boolean().optional(),
  })
  .passthrough();

export const Route = createFileRoute("/concepts/$conceptId/analyze")({
  component: AnalyzePage,
  validateSearch: (search) => analyzeSearchSchema.parse(search),
});

function AnalyzePage() {
  usePageView("/concepts/analyze");
  const { conceptId } = Route.useParams();
  // Same cancels as Curate: a pill started there stays cancellable here.
  const { cancelScoring } = useScoring(conceptId);
  const { cancelRetrieval } = usePdfRetrieval(conceptId);
  const { cancelMonitor } = useMonitorRun(conceptId);

  // Consume pending-spotlight key left by JourneyBar's Show Me handler
  useSpotlight();

  // Auto-open Landscape overlay if returning from Expand page
  useEffect(() => {
    const key = "paperchase-landscape-auto-open";
    if (sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key);
      useLandscapeStore.getState().open();
    }
  }, []);

  const { data: tagsData } = useTags(conceptId);

  // Apply shared filters + working set from Expand page. Defaults match
  // Expand's local fallback (`expand.tsx:490-491`) so the two views stay
  // consistent even when the store's sort is unset — `useFilteredPapers`
  // silently skips sorting on undefined sortField, which surfaces as a
  // randomly-ordered corpus on Analyze when the store happens to hold no
  // explicit sort (preset with null sort_field, sort-header toggle off,
  // etc.).
  const storedState = useFilterStore((s) => s.filters[conceptId]);
  const expandFilters = storedState?.filters ?? {};
  const expandSortField = storedState?.sortField ?? "relevance_score";
  const wsId = storedState?.wsId;
  const paperIdScope = storedState?.paperIdScope;
  const paperIdScopeLabel = storedState?.paperIdScopeLabel;
  const paperIdScopeKind = storedState?.paperIdScopeKind;
  const clearPaperIdScope = useFilterStore((s) => s.clearPaperIdScope);
  const setStoredFilters = useFilterStore((s) => s.setFilters);
  const wsActions = useWorkingSetActions(conceptId);
  const { data: workingSets } = useWorkingSets(conceptId);
  const activeWs = wsId ? workingSets?.find((ws) => ws.id === wsId) : undefined;

  // Field-Debate scoping (spec 2026-07-07) — see computeApplyFieldDebateScope
  // for the rule. Computed once here so all three generate call sites
  // (initial run, stale-banner rerun, header Regenerate) stay consistent.
  const applyFieldDebateScope = computeApplyFieldDebateScope(wsId, paperIdScope);

  // Local search overlay — the one interactive filter on Analyze
  const [analyzeSearch, setAnalyzeSearch] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handleSearchChange = useCallback((value: string) => {
    setAnalyzeSearch(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  }, []);

  // Single source of truth for the scope chain, artifact data, staleness,
  // statuses, and the headline CTA (Piece C). Same instance shape feeds the
  // rail nest; this one adds the page's local search overlay.
  const toolState = useAnalyzeToolState(conceptId, { searchText: debouncedSearch });
  const {
    allPapers,
    displayPapers,
    totalPapers,
    displayPaperIds,
    activePaperIds,
    scoredCount,
    statuses,
    debateStale,
    auditStale,
    debateOverlapPercent,
    auditOverlapPercent,
    fieldDebateData,
    cachedComparison,
    chatIndexed,
  } = toolState;

  // Advisor-driven "Next step" guidance for the Analyze idle panel is wired
  // below, after `navigate` is declared (see analyzeRecs / onActRec).

  const handleSaveCampScopeAsWorkingSet = useCallback(() => {
    if (!paperIdScope || !paperIdScopeLabel) return;
    const name = `${paperIdScopeLabel} (Field Debate)`;
    const paperIds = paperIdScope;
    const label = paperIdScopeLabel;
    const kind = paperIdScopeKind;
    const tempId = wsActions.saveAsWorkingSet({
      name,
      paperIds,
      source: kind ?? "field-debate-camp",
      onSuccess: (realWs, tId) => {
        const cur = useFilterStore.getState().filters[conceptId];
        if (cur?.wsId === tId) {
          setStoredFilters(conceptId, {} as PaperFilters, undefined, undefined, realWs.id);
        }
      },
      onError: (e) => {
        if (kind) {
          useFilterStore.getState().setPaperIdScope(conceptId, paperIds, label, kind);
        }
        toast.error(`Couldn't save working set: ${e.message}`);
      },
    });
    clearPaperIdScope(conceptId);
    setStoredFilters(conceptId, {} as PaperFilters, undefined, undefined, tempId);
    toast.success(`Saved ${paperIds.length} papers as "${name}"`);
  }, [
    paperIdScope,
    paperIdScopeLabel,
    paperIdScopeKind,
    conceptId,
    wsActions,
    clearPaperIdScope,
    setStoredFilters,
  ]);

  const handleCompareTension = useCallback(() => {
    if (!paperIdScope || paperIdScopeKind !== "field-debate-tension") return;
    // Pre-fill comparison selection: top up to 5 by score from the scoped union.
    const top = allPapers
      .filter((p) => paperIdScope.includes(p.id))
      .sort((a, b) => (b.normalized_score ?? 0) - (a.normalized_score ?? 0))
      .slice(0, 5);
    useComparisonStore.getState().clearForConcept(conceptId);
    for (const p of top) useComparisonStore.getState().togglePaper(conceptId, p.id);
    setActiveTool("comparison");
  }, [paperIdScope, paperIdScopeKind, allPapers, conceptId]);

  // Analysis tools
  const gapStore = useGapAnalysisStore();
  const gapAnalysisGenerate = useGapAnalysisGenerate(conceptId);
  const gapGenStep = useGapAnalysisGenerationStore((s) => s.step);
  const gapGenStepLabel = useGapAnalysisGenerationStore((s) => s.stepLabel);
  const gapGenTotalSteps = useGapAnalysisGenerationStore((s) => s.totalSteps);
  const gapGenConceptId = useGapAnalysisGenerationStore((s) => s.conceptId);
  // Gap-fill (Phase 2 — per-gap Find Papers)
  const gapFillGenerate = useGapFillGenerate(conceptId);
  const gapFillIsOpen = useGapFillStore((s) => s.isOpen);
  const gapFillGap = useGapFillStore((s) => s.gap);
  const gapFillCandidates = useGapFillStore((s) => s.candidates);
  const closeGapFillModal = useGapFillStore((s) => s.closeModal);
  // Only show results for the current concept
  const gapAnalysis = {
    ...gapStore,
    isPending: gapStore.isPending && gapStore.conceptId === conceptId,
    data: gapStore.conceptId === conceptId ? gapStore.data : null,
    fingerprint: gapStore.conceptId === conceptId ? gapStore.fingerprint : null,
  };
  const { data: concept } = useConcept(conceptId);

  // Gating: Field Debate + Coverage Audit (gap analysis) are NOT hard-gated —
  // free users get a trial allotment. We never hide the tool; once the trial
  // is exhausted we swap the generate CTA for an UpgradeCard. `paid` is false
  // while currentUser loads, so guard the "exhausted" derivation on currentUser
  // to avoid flashing an UpgradeCard before usage is known.
  const { data: currentUser } = useCurrentUser();
  const paid = isPaidPlan(currentUser?.plan);
  const fdUsed = currentUser?.usage.field_debate_trials_used ?? 0;
  const fdLimit = currentUser?.plan_limits.field_debate_trials ?? null;
  const fdTrialExhausted =
    !!currentUser && !paid && fdLimit != null && fdUsed >= fdLimit;
  const fdTrialRemaining =
    !!currentUser && !paid && fdLimit != null && fdUsed < fdLimit;
  const gapUsed = currentUser?.usage.gap_analysis_trials_used ?? 0;
  const gapLimit = currentUser?.plan_limits.gap_analysis_trials ?? null;
  const gapTrialExhausted =
    !!currentUser && !paid && gapLimit != null && gapUsed >= gapLimit;
  const gapTrialRemaining =
    !!currentUser && !paid && gapLimit != null && gapUsed < gapLimit;

  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  // Coverage Audit -> "Add as an angle" (facets 1c-3). Hooks stay above any early return.
  const angles = useAngles(conceptId);
  const [angleGap, setAngleGap] = useState<Gap | null>(null);
  const maxFacets = currentUser?.plan_limits?.max_facets;
  const angleAtCap = maxFacets != null && angles.facets.length >= maxFacets;

  // Advisor-driven "Next step" guidance for the Analyze idle panel — same engine
  // as Curate's frontier ➤ and the Where-am-I overlay. analyzeRecs[0] also
  // drives the overview card hiding and the rail ➤ (one source of truth).
  // Acting navigates with the rec's search params, which the ?activeTool=
  // deep-link effect turns into open/run.
  const analyzeRecs = useAnalyzeRecs(conceptId, {
    debateNeedsRefresh: toolState.debateNeedsRefresh,
  });
  const onActRec = useCallback(
    (rec: WorkflowRecommendation) => {
      navigate({
        to: rec.action.to,
        params: { conceptId },
        search: rec.action.search ?? {},
      } as never);
    },
    [navigate, conceptId],
  );

  const analyzeDebateMap = fieldDebateData;
  // Slim paper list shared by the FD panels: out-of-scope complement and the
  // camp drill-down's title/score lookup.
  const fdPaperList = useMemo(
    () =>
      displayPapers.map((p) => ({
        id: p.id,
        title: p.title ?? "",
        normalized_score: p.normalized_score ?? 0,
      })),
    [displayPapers],
  );
  // Out-of-scope listing for the FD panel: derived from CURRENT scores via
  // the same mirror as staleness (near-misses first). Count-only ScopeMeta
  // on the map means this is the only source of the actual papers.
  const fdOutOfScope = useMemo(
    () =>
      fieldDebateData?.scope
        ? outOfScopePapers(fdPaperList, fieldDebateData.scope)
        : undefined,
    [fdPaperList, fieldDebateData?.scope],
  );
  const setPaperIdScope = useFilterStore((s) => s.setPaperIdScope);

  // Effect A: store → URL (write scope param when camp scope is active).
  // Guard on `analyzeDebateMap`: on hard refresh of /analyze?scope=…, the
  // store has no `paperIdScopeKind` yet (no persist middleware) and the
  // debateMap query is still in flight. Without this guard, Effect A
  // fires first, sees `!paperIdScopeKind` + URL scope, and strips the
  // URL before Effect B can decode it. After the strip, Effect B's
  // `!search.scope` guard traps it permanently and the page drops to
  // Full Corpus despite the URL having claimed a scope.
  useEffect(() => {
    if (!analyzeDebateMap) return;
    if (!paperIdScopeKind) {
      if (search.scope) {
        navigate({ search: (prev) => ({ ...prev, scope: undefined }), replace: true });
      }
      return;
    }
    const encoded = encodeScopeForUrl(paperIdScopeKind, paperIdScope, paperIdScopeLabel, analyzeDebateMap);
    if (encoded && encoded !== search.scope) {
      navigate({ search: (prev) => ({ ...prev, scope: encoded }), replace: true });
    }
  }, [paperIdScopeKind, paperIdScope, paperIdScopeLabel, analyzeDebateMap, search.scope, navigate]);

  // Effect B: URL → store (decode on mount + debateMap load)
  useEffect(() => {
    if (!search.scope || !analyzeDebateMap) return;
    const decoded = decodeScopeFromUrl(search.scope, analyzeDebateMap);
    if (!decoded) return;
    // Only apply if store doesn't already match (avoids effect cascade)
    const current = useFilterStore.getState().filters[conceptId];
    if (current?.paperIdScopeLabel === decoded.label) return;
    setPaperIdScope(conceptId, decoded.paperIds, decoded.label, decoded.kind);
  }, [search.scope, analyzeDebateMap, conceptId, setPaperIdScope]);

  // Drill enactment: any path that activates a camp scope (nudge, debate-view
  // camp click, deep link) is a "drill into camp" — log once per activation
  // so the backend drill_enacted cycle signal can light the subway route.
  const lastDrillKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (paperIdScopeKind !== "field-debate-camp") {
      lastDrillKeyRef.current = null;
      return;
    }
    const key = `${conceptId}:${paperIdScopeLabel ?? "camp"}`;
    if (lastDrillKeyRef.current === key) return;
    lastDrillKeyRef.current = key;
    trackEvent("feature_use", { feature: "camp_drill", concept_id: conceptId });
  }, [paperIdScopeKind, paperIdScopeLabel, conceptId]);

  const debateGenStore = useFieldDebateGenerationStore();
  const debateGenerate = useFieldDebateGenerate(conceptId);
  const { data: debateQuota } = useFieldDebateQuota(conceptId);
  useFieldDebateRefreshLanded(conceptId, fieldDebateData);
  const generateDebate = {
    isPending: debateGenStore.isPending && debateGenStore.conceptId === conceptId,
    run: debateGenerate.start,
    refresh: debateGenerate.refresh,
    cancel: debateGenerate.cancel,
  };

  const [debateElapsed, setDebateElapsed] = useState(0);

  const [selectedPaper, setSelectedPaper] = useState<PaperWithRole | null>(null);
  // Hydrate heavy fields (abstract / notes / relevance_justification)
  // for the selected paper — the trawled list above is slim. Falls back
  // to the slim row while the fetch is in flight so PaperDetail mounts
  // with the correct title immediately. `isHydrating` drives skeletons in
  // PaperDetail so empty heavy fields don't read as "no data".
  const {
    data: hydratedSelectedPaper,
    isFetching: isHydratingSelectedPaper,
  } = useConceptPaper(conceptId, selectedPaper?.id);
  // Only treat hydrated data as authoritative if it matches the currently
  // selected row — otherwise show the slim row and let `isHydrating` flag
  // missing heavy fields as in-flight.
  const matchedHydration =
    hydratedSelectedPaper && hydratedSelectedPaper.id === selectedPaper?.id
      ? hydratedSelectedPaper
      : null;
  const detailPaper = matchedHydration ?? selectedPaper;
  const isHydratingDetail =
    !!selectedPaper && !matchedHydration && isHydratingSelectedPaper;

  // Notes editing on the Analyze detail pane (mirrors Expand). Without this
  // the notes section is hidden entirely (gated on `onNotesChange` in
  // PaperDetail) and the user can't see notes they wrote elsewhere.
  const notesMutation = useNotes(conceptId);
  const handleNotesChange = useCallback(
    (paperId: string, notes: string) => {
      notesMutation.mutate(
        { paperId, notes },
        {
          onSuccess: (updated) => {
            setSelectedPaper((cur) =>
              cur && cur.id === paperId ? { ...cur, notes: updated.notes } : cur,
            );
          },
        },
      );
    },
    [notesMutation],
  );
  const [gapElapsed, setGapElapsed] = useState(0);
  const gapDepth = 10;
  const [activeTool, setActiveTool] = useState<ActiveTool>(null);
  const selectedComparisonIds = useComparisonStore(
    (s) => s.byConcept[conceptId]?.selectedPaperIds,
  );
  const inComparisonResultMode =
    activeTool === "comparison" && !!cachedComparison;

  // Scope-strip table toggle — visible by default; persisted per-concept.
  // Tri-state localStorage: "0" = user-collapsed, "1" = user-expanded, absent = default (expanded).
  const ANALYZE_TABLE_KEY = `paperchase-analyze-table-${conceptId}`;
  const readTableExpanded = useCallback((): boolean => {
    try {
      return localStorage.getItem(ANALYZE_TABLE_KEY) !== "0";
    } catch {
      return true;
    }
  }, [ANALYZE_TABLE_KEY]);
  const [tableExpanded, setTableExpanded] = useState<boolean>(() =>
    readTableExpanded(),
  );
  const handleTableToggle = useCallback(
    (next: boolean) => {
      setTableExpanded(next);
      trackEvent("analyze_table_toggle", { expanded: next });
      try {
        localStorage.setItem(ANALYZE_TABLE_KEY, next ? "1" : "0");
      } catch {
        /* ignore — Safari private mode etc. */
      }
    },
    [ANALYZE_TABLE_KEY],
  );

  // When concept changes, the localStorage key changes — re-read to avoid stale state
  useEffect(() => {
    setTableExpanded(readTableExpanded());
  }, [readTableExpanded]);

  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  const handleShare = useCallback(async () => {
    setSharing(true);
    try {
      const res = await apiClient
        .post(`api/concepts/${conceptId}/share`)
        .json<{ share_url: string }>();
      setShareUrl(res.share_url);
      // Clipboard write can throw "Document is not focused" when the
      // Popover steals focus on click — don't let it mask API success.
      try {
        await navigator.clipboard.writeText(res.share_url);
        toast.success("Share link copied to clipboard");
      } catch {
        toast.success("Share link created — use Copy link to copy");
      }
    } catch (err) {
      toast.error("Failed to create share link");
    } finally {
      setSharing(false);
    }
  }, [conceptId]);

  const handleRevoke = useCallback(async () => {
    try {
      await apiClient.delete(`api/concepts/${conceptId}/share`);
      setShareUrl(null);
      toast.success("Share link revoked");
    } catch {
      toast.error("Failed to revoke share link");
    }
  }, [conceptId]);

  // Elapsed timer for gap analysis
  useEffect(() => {
    if (!gapAnalysis.isPending) {
      setGapElapsed(0);
      return;
    }
    setGapElapsed(0);
    const interval = setInterval(() => setGapElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, [gapAnalysis.isPending]);

  // Elapsed timer for debate generation
  useEffect(() => {
    if (!generateDebate.isPending) {
      setDebateElapsed(0);
      return;
    }
    setDebateElapsed(0);
    const interval = setInterval(() => setDebateElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, [generateDebate.isPending]);

  // Reset chat/thread state when switching concepts
  const prevConceptId = useRef(conceptId);
  useEffect(() => {
    if (prevConceptId.current !== conceptId) {
      const oldConceptId = prevConceptId.current;
      prevConceptId.current = conceptId;
      useChatStore.getState().clearChat();
      useThreadStore.getState().setActiveThreadId(null);
      useComparisonStore.getState().clearForConcept(oldConceptId);
    }
  }, [conceptId]);

  // Mark coverage audit as used only when the run completes successfully.
  const prevGapDataRef = useRef<typeof gapAnalysis.data>(null);
  useEffect(() => {
    const prev = prevGapDataRef.current;
    const curr = gapAnalysis.data;
    if (curr && !prev) {
      guideSignals.mark(conceptId, 'hasRunCoverageAudit');
    }
    prevGapDataRef.current = curr;
  }, [gapAnalysis.data, conceptId]);

  // Persist scroll position across navigation
  const savedScrollTop = useDebateStore((s) => s.scrollTop);
  const setScrollTop = useDebateStore((s) => s.setScrollTop);
  const scrollRef = useRef(0);
  useEffect(() => {
    // Restore on mount (after a frame so content has rendered)
    if (savedScrollTop > 0) {
      requestAnimationFrame(() => window.scrollTo(0, savedScrollTop));
    }
    // Track scroll continuously
    const onScroll = () => { scrollRef.current = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Save on unmount from the ref (window.scrollY may be 0 by then)
    return () => {
      window.removeEventListener("scroll", onScroll);
      setScrollTop(scrollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportCSV = useCallback(async () => {
    if (displayPapers.length === 0) return;
    // Slim list omits abstract — refetch full shape for the in-scope set
    // so the CSV's Abstract column is meaningful.
    try {
      const ids = new Set(displayPapers.map((p) => p.id));
      const params = new URLSearchParams({
        role: "trawled",
        per_page: "5000",
      });
      const full = await apiClient
        .get(`api/concepts/${conceptId}/papers?${params.toString()}`)
        .json<{ papers: PaperWithRole[] }>();
      const rows = full.papers.filter((p) => ids.has(p.id));
      exportPapersToCSV(rows, "paperchase-analyze-papers.csv");
    } catch {
      // Fall back to slim rows — better an abstract-less export than none.
      exportPapersToCSV(displayPapers, "paperchase-analyze-papers.csv");
    }
  }, [displayPapers, conceptId]);

  const handleRunCoverageAudit = useCallback(() => {
    if (displayPapers.length < 10) return;
    setActiveTool("audit");
    if (gapAnalysis.data || gapAnalysis.isPending) return;
    trackEvent("feature_use", { feature: "coverage_audit", action: "started" });
    // Audit scope matches what the scope strip shows — filtered display set,
    // not the wider working-set corpus. The histogram + "62 of 86 active"
    // count above the trigger frames the run.
    const paperIds = displayPapers.map((p) => p.id);
    const fingerprint = computeFingerprint({
      paperIds,
      wsId: wsId ?? null,
      scoredCount,
    });
    // Mark legacy store as running with the snapshot — useGapAnalysisGenerate's
    // onComplete bridges back via setResult. Synchronous endpoint retired
    // in Task 13; new flow is fully SSE-driven.
    gapStore.markRunning(conceptId, fingerprint);
    gapAnalysisGenerate.start({
      max_papers_per_cluster: gapDepth,
      paper_ids: paperIds,
    });
  }, [
    displayPapers,
    gapAnalysis.data,
    gapAnalysis.isPending,
    gapStore,
    gapAnalysisGenerate,
    conceptId,
    gapDepth,
    wsId,
    scoredCount,
  ]);

  const handleRunFieldDebate = useCallback(() => {
    // If cached data exists, just open the panel — the scoredCount gate
    // only applies to NEW generation. This lets users still view a prior
    // analysis when their current scope (e.g. a small Working Set saved
    // from a camp) drops below 15 papers.
    if (fieldDebateData) {
      setActiveTool("debate");
      return;
    }
    if (scoredCount < 15) return;
    setActiveTool("debate");
    if (generateDebate.isPending) return;
    trackEvent("feature_use", { feature: "field_debate", action: "started" });
    // Pass displayPaperIds so the debate analyzes the same set the scope
    // strip describes (mirrors Coverage Audit). Without this the backend
    // analyzes the wider trawled+ws scope and camps sum to far more papers
    // than the user expects.
    generateDebate.run({
      ws_id: wsId,
      paper_ids: displayPaperIds,
      apply_scope: applyFieldDebateScope,
    });
  }, [
    scoredCount,
    fieldDebateData,
    generateDebate,
    wsId,
    displayPaperIds,
    applyFieldDebateScope,
  ]);

  // Effect: ?activeTool= deep-link → open the matching panel (rail nest,
  // Curate nudges, Expand card D). OPEN-only — running needs &autorun=true
  // (the advisor's "Run coverage audit" / "Run Field Debate" Next-step recs),
  // so a rail click can never start an LLM job. Param is consumed-and-stripped
  // so refresh doesn't re-fire. Autorun waits for the papers query: consuming
  // it on a cold load would hit the run guards against an EMPTY list and
  // silently lose the run (the effect re-fires when papersLoaded flips).
  useEffect(() => {
    const t = search.activeTool;
    if (!t) return;
    if (search.autorun && (t === "audit" || t === "debate")) {
      if (!toolState.papersLoaded) return; // keep params until data is in
      setActiveTool(t); // open even when the run guard declines
      if (t === "audit") handleRunCoverageAudit(); // guards on existing data / in-flight
      else handleRunFieldDebate(); // guards on scoredCount / cached / in-flight
    } else {
      setActiveTool(t);
    }
    if (t === "debate" || t === "audit") setTableExpanded(true);
    navigate({
      search: (prev) => ({ ...prev, activeTool: undefined, autorun: undefined }),
      replace: true,
    });
  }, [
    search.activeTool,
    search.autorun,
    toolState.papersLoaded,
    handleRunCoverageAudit,
    handleRunFieldDebate,
    navigate,
  ]);

  // Scope-strip props — count, score histogram, year range, camp mix.
  const stripProps = useMemo(() => {
    const scores = displayPapers
      .map((p) => p.relevance_score ?? 0)
      .filter((s) => s > 0);
    const years = displayPapers
      .map((p) => p.year)
      .filter((y): y is number => y != null && y > 0);
    const yearRange: [number, number] | null = years.length
      ? [Math.min(...years), Math.max(...years)]
      : null;
    const supportCount = displayPapers.filter(
      (p) => p.argumentation_role === "supports",
    ).length;
    const counterCount = displayPapers.filter(
      (p) => p.argumentation_role === "challenges",
    ).length;
    return {
      paperCount: displayPapers.length,
      totalCount: allPapers.length,
      scores,
      yearRange,
      supportCount,
      counterCount,
    };
  }, [displayPapers, allPapers.length]);

  // Phase signal from persisted userStorage — analyze.tsx isn't a counts
  // host, so the localStorage truth is what we route on for data-guide-phase
  // body attribute (used by <FeatureRef>).
  const persistedPhase = usePersistedGuidePhase();

  // Expose current guide phase on document.body so <FeatureRef> can route
  // clicks to the right surface (event vs. drawer).
  useEffect(() => {
    document.body.setAttribute("data-guide-phase", persistedPhase);
    return () => {
      document.body.removeAttribute("data-guide-phase");
    };
  }, [persistedPhase]);

  // Client-side preview of what POST /refresh will do — same threshold+cap
  // mirror as staleness, so the banner counts can't disagree with the server.
  const debateRefreshCounts = useMemo(
    () =>
      debateRefreshDiff(
        displayPapers,
        fieldDebateData?.scope,
        fieldDebateData?.fingerprint,
      ),
    [displayPapers, fieldDebateData],
  );
  // A working-set scope change means REBUILD, not refresh: camps were derived
  // from a scope, the backend 409s cross-scope refreshes, and the diff counts
  // below are meaningless across scopes (Codex review 2026-07-10).
  const debateWsScopeChanged = debateScopeChanged(
    wsId,
    fieldDebateData?.fingerprint,
  );
  const debateRefreshLabel = debateWsScopeChanged
    ? "Rebuild — scope changed"
    : `Refresh — ${debateRefreshCounts.added} new paper${
        debateRefreshCounts.added === 1 ? "" : "s"
      }${debateRefreshCounts.removed ? `, ${debateRefreshCounts.removed} departed` : ""}`;
  // Heavy-drift advisory: when the additions exceed 30% of the current scope,
  // an incremental refresh (frozen camps) may misrepresent the field — nudge
  // toward Rebuild without blocking Refresh. Suppressed on a scope change —
  // the action already IS a rebuild there.
  const debateRefreshAdvisory = useMemo(() => {
    if (debateWsScopeChanged) return undefined;
    const scopeSize = debateComparisonPaperIds(
      displayPapers,
      fieldDebateData?.scope,
    ).length;
    return debateRefreshCounts.added > DRIFT_ADVISORY_PCT * scopeSize
      ? "A lot has changed — consider Rebuild to re-derive camps."
      : undefined;
  }, [debateRefreshCounts, displayPapers, fieldDebateData, debateWsScopeChanged]);

  // Stale-banner action handlers. Telemetry distinguishes the contextual
  // action (the user reacted to the amber banner / stale headline) from the
  // generic Rebuild button and from rail / initial-promotion triggers.
  //
  // Field Debate's stale action is now REFRESH (FD Stage 3): incremental —
  // assigns entered papers into the frozen camps, drops departed ones. The
  // old 15-scored-paper guard is gone on purpose: it protects camp
  // DERIVATION, and refresh never derives camps.
  // Confirm-gated full rebuild — shared by the header Rebuild button and the
  // scope-changed branch of the stale banner (same copy, one source).
  const handleRebuildDebate = useCallback(() => {
    if (!confirm(rebuildConfirmMessage(debateQuota))) return;
    generateDebate.run({
      ws_id: wsId,
      paper_ids: displayPaperIds,
      apply_scope: applyFieldDebateScope,
    });
  }, [debateQuota, generateDebate, wsId, displayPaperIds, applyFieldDebateScope]);

  const [rerunChoiceOpen, setRerunChoiceOpen] = useState(false);
  const runDebateRefresh = useCallback(
    (mode: "refresh" | "refresh-from-choice") => {
      trackEvent("analyze_stale_rerun_click", {
        tool: "field-debate",
        overlap_percent: debateOverlapPercent,
        mode,
      });
      generateDebate.refresh({
        ws_id: wsId,
        paper_ids: displayPaperIds,
        apply_scope: applyFieldDebateScope,
      });
    },
    [debateOverlapPercent, generateDebate, wsId, displayPaperIds, applyFieldDebateScope],
  );
  // Rebuild chosen from the choice dialog: the dialog IS the confirm step
  // (it already states the shuffle warning + quota), so bypass the native
  // confirm that handleRebuildDebate wraps.
  const runDebateRebuildFromChoice = useCallback(() => {
    trackEvent("analyze_stale_rerun_click", {
      tool: "field-debate",
      overlap_percent: debateOverlapPercent,
      mode: "rebuild-from-choice",
    });
    generateDebate.run({
      ws_id: wsId,
      paper_ids: displayPaperIds,
      apply_scope: applyFieldDebateScope,
    });
  }, [debateOverlapPercent, generateDebate, wsId, displayPaperIds, applyFieldDebateScope]);

  const handleStaleRerunDebate = useCallback(() => {
    if (debateWsScopeChanged) {
      // Cross-scope: refresh would 409 (camps were derived from the old
      // scope) — route to the confirm-gated rebuild instead.
      trackEvent("analyze_stale_rerun_click", {
        tool: "field-debate",
        overlap_percent: debateOverlapPercent,
        mode: "rebuild-scope-changed",
      });
      handleRebuildDebate();
      return;
    }
    if (debateRefreshAdvisory) {
      // Heavy drift: put the Refresh-vs-Rebuild decision BEFORE the action
      // instead of after it (the old flow fired a refresh, then showed the
      // "consider Rebuild" nudge on the view the user landed on).
      trackEvent("analyze_stale_rerun_click", {
        tool: "field-debate",
        overlap_percent: debateOverlapPercent,
        mode: "choice-prompt",
      });
      setRerunChoiceOpen(true);
      return;
    }
    runDebateRefresh("refresh");
  }, [
    debateWsScopeChanged,
    debateRefreshAdvisory,
    handleRebuildDebate,
    debateOverlapPercent,
    runDebateRefresh,
  ]);

  // Failed-batch-build Retry (FieldDebateView status bar). NOT refresh: the
  // map is still `partial` after a failed build and POST /refresh 409s on
  // partial maps by design — retry must re-run the build itself.
  const handleRetryDebateBuild = useCallback(() => {
    trackEvent("analyze_stale_rerun_click", {
      tool: "field-debate",
      overlap_percent: debateOverlapPercent,
      mode: "retry",
    });
    generateDebate.run({
      ws_id: wsId,
      paper_ids: displayPaperIds,
      apply_scope: applyFieldDebateScope,
    });
  }, [
    debateOverlapPercent,
    generateDebate,
    wsId,
    displayPaperIds,
    applyFieldDebateScope,
  ]);

  const handleStaleRerunAudit = useCallback(() => {
    trackEvent("analyze_stale_rerun_click", {
      tool: "coverage-audit",
      overlap_percent: auditOverlapPercent,
    });
    const fingerprint = computeFingerprint({
      paperIds: displayPaperIds,
      wsId: wsId ?? null,
      scoredCount,
    });
    gapStore.markRunning(conceptId, fingerprint);
    gapAnalysisGenerate.start({
      max_papers_per_cluster: gapDepth,
      paper_ids: displayPaperIds,
    });
  }, [
    auditOverlapPercent,
    gapStore,
    gapAnalysisGenerate,
    conceptId,
    gapDepth,
    displayPaperIds,
    wsId,
    scoredCount,
  ]);

  // Artifact cards for the idle-state Overview panel (replaces the right
  // rail's ToolCards). Status/lock logic comes from the shared hook; this
  // memo only adds page-local actions (open / stale-rerun) and summaries.
  const overviewArtifacts: ArtifactCard[] = useMemo(() => {
    const fmtDate = (iso: string | undefined) =>
      iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
    const open = (tool: Exclude<ActiveTool, null>) => () => {
      trackEvent("analyze_tool_click", { tool, from: "overview" });
      setActiveTool(tool);
    };
    // Card order mirrors TOOL_ORDER: corpus-wide diagnostics first (debate,
    // audit), then focused/interactive tools, then the two ↗ handoffs.
    const cards: ArtifactCard[] = [
      {
        id: "field-debate",
        ...TOOL_META["field-debate"],
        status: statuses["field-debate"].status,
        lockedReason: statuses["field-debate"].lockedReason,
        summary:
          statuses["field-debate"].status === "running"
            ? toolState.debateBuildInFlight && fieldDebateData?.head_paper_count
              ? `Top ${fieldDebateData.head_paper_count} placed · assigning the remaining papers — usually a few minutes.`
              : "Generating now…"
            : statuses["field-debate"].status === "refreshing"
              ? `Refreshing — ${
                  debateRefreshCounts.added > 0
                    ? `${debateRefreshCounts.added} new paper${debateRefreshCounts.added === 1 ? "" : "s"}`
                    : "new papers"
                } slotting into the existing camps, usually a few minutes.`
              : fieldDebateData
                ? `${
                    fieldDebateData.refresh_meta?.refreshed_at
                      ? `Refreshed ${fmtDate(fieldDebateData.refresh_meta.refreshed_at) ?? "recently"}${
                          fieldDebateData.camps_generated_at
                            ? ` · camps from ${fmtDate(fieldDebateData.camps_generated_at)}`
                            : ""
                        }`
                      : `Generated ${fmtDate(fieldDebateData.generated_at) ?? "earlier"}`
                  } — ${fieldDebateData.camps.length} camps · ${fieldDebateData.tensions.length} tensions${statuses["field-debate"].status === "stale" ? " · corpus has shifted since" : ""}`
                : "Not run yet.",
        onOpen: open("debate"),
        onRerun:
          statuses["field-debate"].status === "stale" ? handleStaleRerunDebate : undefined,
        rerunPending: generateDebate.isPending,
        // Say what the action IS before the user clicks: the same computed
        // label + drift nudge the in-view stale banner shows.
        rerunLabel:
          statuses["field-debate"].status === "stale" ? debateRefreshLabel : undefined,
        advisory:
          statuses["field-debate"].status === "stale" ? debateRefreshAdvisory : undefined,
      },
      {
        id: "coverage-audit",
        ...TOOL_META["coverage-audit"],
        status: statuses["coverage-audit"].status,
        lockedReason: statuses["coverage-audit"].lockedReason,
        summary:
          statuses["coverage-audit"].status === "running"
            ? "Auditing now…"
            : gapAnalysis.data
              ? `Audit complete — ${gapAnalysis.data.gaps.length} gaps across ${gapAnalysis.data.cluster_count} clusters${statuses["coverage-audit"].status === "stale" ? " · corpus has shifted since" : ""}`
              : "Not run yet.",
        onOpen: statuses["coverage-audit"].status === "locked" ? undefined : open("audit"),
        onRerun:
          statuses["coverage-audit"].status === "stale" ? handleStaleRerunAudit : undefined,
        rerunPending: gapAnalysis.isPending,
      },
      {
        id: "comparison",
        ...TOOL_META.comparison,
        status: statuses.comparison.status,
        lockedReason: statuses.comparison.lockedReason,
        summary:
          statuses.comparison.status === "running"
            ? "Generating comparison…"
            : cachedComparison
              ? `${cachedComparison.rows.length} papers compared · ${fmtDate(cachedComparison.generated_at) ?? ""}`
              : "No comparison yet — select 2+ papers to compare.",
        onOpen: statuses.comparison.status === "locked" ? undefined : open("comparison"),
      },
      {
        id: "chat",
        ...TOOL_META.chat,
        status: statuses.chat.status,
        summary: chatIndexed
          ? "Index built — ready for questions."
          : "Index not built yet — the chat panel builds it on first use.",
        onOpen: open("chat"),
      },
      {
        id: "landscape",
        ...TOOL_META.landscape,
        status: statuses.landscape.status,
        lockedReason: statuses.landscape.lockedReason,
        summary:
          statuses.landscape.status === "done" || statuses.landscape.status === "stale"
            ? `Generated this session${statuses.landscape.status === "stale" ? " — corpus has shifted since" : ""}.`
            : "Not generated this session.",
        onOpen:
          statuses.landscape.status === "locked"
            ? undefined
            : () => useLandscapeStore.getState().open(),
      },
      {
        id: "critique",
        ...TOOL_META.critique,
        status: statuses.critique.status,
        lockedReason: statuses.critique.lockedReason,
        summary: (() => {
          if (statuses.critique.status === "locked") return "Not available yet.";
          const half = (label: string, s: ToolStatusResult) =>
            s.status === "done"
              ? `${label} ✓`
              : s.status === "stale"
                ? `${label} stale`
                : s.status === "running"
                  ? `${label} running…`
                  : `${label} not run`;
          return `${half("Soundness", toolState.critique.soundness)} · ${half(
            "Position", toolState.critique.position,
          )} · opens in Build.`;
        })(),
        onOpen:
          statuses.critique.status === "locked"
            ? undefined
            : () =>
                navigate({
                  to: "/concepts/$conceptId/build",
                  params: { conceptId },
                  search: { view: "map", critique: true },
                }),
      },
    ];
    return cards.filter((c) => !shouldHideFromRail(c.id, analyzeRecs[0]));
  }, [
    statuses,
    fieldDebateData,
    handleStaleRerunDebate,
    generateDebate.isPending,
    debateRefreshCounts,
    debateRefreshLabel,
    debateRefreshAdvisory,
    chatIndexed,
    cachedComparison,
    gapAnalysis.data,
    gapAnalysis.isPending,
    handleStaleRerunAudit,
    toolState.critique,
    toolState.debateBuildInFlight,
    analyzeRecs,
    navigate,
    conceptId,
  ]);

  return (
    <div className="space-y-4">
      {/* Page-level toolbar — chat-index lifecycle moved into ChatToolPanel,
          so only Export CSV remains here. Empty when no papers. */}
      <div className="flex items-center gap-3">
        {displayPapers.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExportCSV}
            aria-label="Export chat papers to CSV"
          >
            <FileDown className="size-4" />
            Export CSV
          </Button>
        )}
      </div>

      {/* Scope cluster — filter context (lens) + corpus shape (readout)
          visually unified into a single bordered panel. Top row carries
          active-filter chip + search input; bottom row is the scope strip
          with count, histogram, year range, camp mix, and table toggle. */}
      <div className="rounded-md border overflow-hidden">
        <div className="flex items-center gap-3 bg-muted/30 border-b px-3.5 py-2">
          <ActiveFiltersBadge
            filters={expandFilters}
            sortField={expandSortField}
            conceptId={conceptId}
            tags={tagsData}
            conceptName={concept?.name}
          />
          <div className="relative ml-auto">
            <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search papers..."
              value={analyzeSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="h-7 w-48 pl-8 text-xs"
            />
          </div>
        </div>
        <AnalyzeScopeStrip
          {...stripProps}
          expanded={tableExpanded}
          onToggle={handleTableToggle}
        />
      </div>

      {/* Main layout: papers left (when expanded), chat center, rail right */}
      <div className="flex gap-4" style={{ height: "calc(100vh - 320px)" }}>
        {/* Inspect pane — single slot. Compact paper list collapses into the
            paper detail when a row is selected; both share the same column
            so the chat surface keeps maximum width. */}
        {tableExpanded && !inComparisonResultMode && (
          <div className="w-[38%] shrink-0 overflow-hidden rounded-md border">
            {selectedPaper && detailPaper ? (
              <PaperDetail
                paper={detailPaper}
                onClose={() => setSelectedPaper(null)}
                isHydrating={isHydratingDetail}
                onNotesChange={handleNotesChange}
                onSetUpLibraryLink={() => navigate({ to: "/settings/integrations" })}
              />
            ) : (
              <>
                <CampScopeBanner
                  label={paperIdScopeLabel}
                  kind={paperIdScopeKind}
                  paperCount={paperIdScope?.length ?? 0}
                  onSaveAsWorkingSet={handleSaveCampScopeAsWorkingSet}
                  onCompareTension={handleCompareTension}
                  onExit={() => clearPaperIdScope(conceptId)}
                />
                <div className="border-b px-3 py-2 flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Papers in scope ({displayPapers.length}
                    {displayPapers.length < totalPapers ? ` of ${totalPapers}` : ""})
                  </h3>
                  {activeWs && (
                    <span className="text-xs text-[#89b4fa] truncate" title={activeWs.name}>
                      · {activeWs.name}
                    </span>
                  )}
                </div>
                <div className="h-[calc(100%-37px)]">
                  <AnalyzePaperList
                    papers={displayPapers}
                    onRowClick={setSelectedPaper}
                    selectionMode={activeTool === "comparison"}
                    selectedIds={
                      activeTool === "comparison"
                        ? selectedComparisonIds
                        : undefined
                    }
                    onToggleSelect={(pid) =>
                      useComparisonStore.getState().togglePaper(conceptId, pid)
                    }
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Active tool panel — chat / audit / debate / comparison render here.
            When activeTool is null, the AnalyzeOverview (idle state) renders via the idle prop. */}
        <div className="flex-1 min-w-0" data-guide-target="dynamicChatPanel">
          <AnalyzeActivePanel
            activeTool={activeTool}
            onClose={() => {
              // X on the panel header should also cancel any in-flight
              // analysis for that tool. Without this, closing the FD panel
              // mid-regen leaves the pipeline running invisibly (only the
              // OperationBar pill reflects it) — the user reasonably
              // expects "X" to mean "stop and exit", not "exit and keep
              // burning tokens".
              if (activeTool === "debate" && generateDebate.isPending) {
                generateDebate.cancel();
              } else if (activeTool === "audit" && gapAnalysis.isPending) {
                gapAnalysis.cancel();
              }
              setActiveTool(null);
            }}
            idle={
              <AnalyzeOverview
                recs={analyzeRecs}
                onActRec={onActRec}
                artifacts={overviewArtifacts}
              />
            }
            staleness={
              activeTool === "debate" && fieldDebateData && debateStale
                ? {
                    overlapPercent: debateOverlapPercent,
                    onRerun: handleStaleRerunDebate,
                    isPending: generateDebate.isPending,
                    rerunLabel: debateRefreshLabel,
                    advisory: debateRefreshAdvisory,
                  }
                : activeTool === "audit" && gapAnalysis.data && auditStale
                  ? {
                      overlapPercent: auditOverlapPercent,
                      onRerun: handleStaleRerunAudit,
                      isPending: gapAnalysis.isPending,
                    }
                  : null
            }
            headerActions={
              activeTool === "debate" && fieldDebateData ? (
                <>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        variant={shareUrl ? "secondary" : "ghost"}
                        onClick={!shareUrl ? handleShare : undefined}
                        disabled={sharing}
                      >
                        <Link2 className="w-3.5 h-3.5 mr-1" />
                        {sharing ? "Sharing..." : shareUrl ? "Shared" : "Share"}
                      </Button>
                    </PopoverTrigger>
                    {shareUrl && (
                      <PopoverContent align="end" className="w-56 p-2 space-y-2">
                        <button
                          className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted transition-colors"
                          onClick={async () => {
                            await navigator.clipboard.writeText(shareUrl);
                            toast.success("Link copied");
                          }}
                        >
                          <Copy className="size-3.5" />
                          Copy link
                        </button>
                        <button
                          className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                          onClick={() => {
                            if (confirm("This will break all existing shared links. Continue?")) {
                              handleRevoke();
                            }
                          }}
                        >
                          <Trash2 className="size-3.5" />
                          Revoke link
                        </button>
                      </PopoverContent>
                    )}
                  </Popover>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleRebuildDebate}
                    disabled={
                      generateDebate.isPending || rebuildAtLimit(debateQuota)
                    }
                    title={
                      rebuildAtLimit(debateQuota) ? REBUILD_LIMIT_TITLE : undefined
                    }
                  >
                    <RefreshCw
                      className={`w-3.5 h-3.5 mr-1 ${generateDebate.isPending ? "animate-spin" : ""}`}
                    />
                    Rebuild
                  </Button>
                </>
              ) : null
            }
          >
            {activeTool === "chat" && (
              <ChatToolPanel
                conceptId={conceptId}
                paperIds={displayPaperIds}
              />
            )}

            {activeTool === "audit" &&
              (gapAnalysis.data ? (
                <div className="h-full overflow-y-auto">
                  <GapAnalysisPanel
                    data={gapAnalysis.data}
                    onDismiss={() => {
                      gapAnalysis.reset();
                      setActiveTool(null);
                    }}
                    onFindPapers={(gap: Gap, auditId: string) => {
                      void gapFillGenerate.start(auditId, gap);
                    }}
                    onAddAsAngle={angles.enabled ? setAngleGap : undefined}
                    addAngleDisabledReason={
                      angleAtCap ? "Angle limit reached for your plan. Upgrade to add more." : undefined
                    }
                  />
                </div>
              ) : gapAnalysis.isPending ? (
                <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground py-12 px-4">
                  <Sparkles className="size-8 opacity-30" />
                  <span className="text-sm">
                    Auditing {displayPapers.length}
                    {displayPapers.length < allPapers.length
                      ? ` of ${allPapers.length} active`
                      : ""}{" "}
                    paper{displayPapers.length === 1 ? "" : "s"}…{" "}
                    <span className="tabular-nums">
                      {gapElapsed < 60
                        ? `${gapElapsed}s`
                        : `${Math.floor(gapElapsed / 60)}:${(gapElapsed % 60).toString().padStart(2, "0")}`}
                    </span>
                  </span>
                  {gapGenConceptId === conceptId && gapGenStepLabel && (
                    <span className="text-xs">
                      {gapGenStepLabel}{" "}
                      <span className="tabular-nums">
                        Step {Math.max(0, gapGenStep)}/{gapGenTotalSteps}
                      </span>
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void gapAnalysisGenerate.cancel();
                      gapAnalysis.cancel();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : gapTrialExhausted ? (
                <UpgradeCard
                  feature="Coverage Audit"
                  description="Find the gaps in your corpus across topic clusters. Upgrade for unlimited runs."
                />
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 py-12 px-4">
                  <p className="text-sm text-muted-foreground">No audit run yet.</p>
                  <Button
                    onClick={handleRunCoverageAudit}
                    disabled={displayPapers.length < 10}
                    title={
                      displayPapers.length < 10
                        ? `Need 10+ papers (have ${displayPapers.length})`
                        : undefined
                    }
                  >
                    <BarChart3 className="size-4" /> Run Coverage Audit
                  </Button>
                  {displayPapers.length < 10 && (
                    <p className="text-xs text-muted-foreground/70">
                      Need 10+ papers in scope (have {displayPapers.length}).
                    </p>
                  )}
                  {gapTrialRemaining && displayPapers.length >= 10 && (
                    <p className="text-xs text-muted-foreground/70">
                      {Math.max(0, gapLimit! - gapUsed)} free run
                      {gapLimit! - gapUsed === 1 ? "" : "s"} included on your plan.
                    </p>
                  )}
                </div>
              ))}

            {activeTool === "debate" &&
              (fieldDebateData ? (
                <FieldDebateView
                  debateMap={fieldDebateData}
                  conceptId={conceptId}
                  onRetry={handleRetryDebateBuild}
                  outOfScopePapers={fdOutOfScope}
                  papers={fdPaperList}
                />
              ) : generateDebate.isPending ? (
                <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground py-12 px-4">
                  <Swords className="size-8 opacity-30" />
                  <span className="text-sm">
                    Identifying argumentative camps…{" "}
                    <span className="tabular-nums">
                      {debateElapsed < 60
                        ? `${debateElapsed}s`
                        : `${Math.floor(debateElapsed / 60)}:${(debateElapsed % 60).toString().padStart(2, "0")}`}
                    </span>
                  </span>
                  <span className="text-xs opacity-60">This typically takes 60-90 seconds</span>
                </div>
              ) : fdTrialExhausted ? (
                <UpgradeCard
                  feature="Field Debate"
                  description="Map the field's debate structure into camps and tensions. Upgrade for unlimited runs."
                />
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 py-12 px-4">
                  <p className="text-sm text-muted-foreground">No debate map yet.</p>
                  <Button
                    onClick={handleRunFieldDebate}
                    disabled={scoredCount < 15}
                    title={scoredCount < 15 ? `Need 15+ scored (have ${scoredCount})` : undefined}
                  >
                    <Swords className="size-4" /> Generate Field Debate
                  </Button>
                  {scoredCount < 15 && (
                    <p className="text-xs text-muted-foreground/70">
                      Need 15+ scored papers (have {scoredCount}).
                    </p>
                  )}
                  {fdTrialRemaining && scoredCount >= 15 && (
                    <p className="text-xs text-muted-foreground/70">
                      {Math.max(0, fdLimit! - fdUsed)} free run
                      {fdLimit! - fdUsed === 1 ? "" : "s"} included on your plan.
                    </p>
                  )}
                </div>
              ))}

            {activeTool === "comparison" && (
              <div className="h-full overflow-y-auto p-4">
                <ComparisonTable conceptId={conceptId} />
              </div>
            )}
          </AnalyzeActivePanel>
        </div>

      </div>

      <LandscapeOverlay
        conceptId={conceptId}
        conceptName={concept?.name ?? ""}
        paperIdScope={displayPaperIds}
        corpusPaperIds={activePaperIds}
        wsId={wsId ?? null}
      />

      {/* Sticky bottom OperationBar — surfaces in-flight Field Debate
          generation (and other ops) so progress survives page navigation. */}
      <OperationBar
        onCancelPdf={cancelRetrieval}
        onCancelScoring={cancelScoring}
        onCancelMonitor={cancelMonitor}
        conceptId={conceptId}
      />

      {/* Gap-fill candidate review modal — opened by SSE complete event
          from useGapFillGenerate. Accept writes papers + provenance via
          POST /accept and invalidates papers/concept-papers caches. */}
      <GapFillModal
        isOpen={gapFillIsOpen}
        gap={gapFillGap}
        candidates={gapFillCandidates}
        isAccepting={gapFillGenerate.accept.isPending}
        onAccept={(paperIds) => {
          gapFillGenerate.accept.mutate(paperIds);
        }}
        onClose={closeGapFillModal}
      />
      <FieldDebateRerunChoiceDialog
        open={rerunChoiceOpen}
        onOpenChange={setRerunChoiceOpen}
        overlapPercent={debateOverlapPercent}
        added={debateRefreshCounts.added}
        removed={debateRefreshCounts.removed}
        quota={debateQuota}
        onRefresh={() => runDebateRefresh("refresh-from-choice")}
        onRebuild={runDebateRebuildFromChoice}
      />
      {concept && angleGap && (
        <AddAngleSheet
          open
          concept={concept}
          mode={{
            kind: "new",
            prefill: {
              gap: {
                kind: angleGap.kind,
                tag: angleGap.tag,
                description: angleGap.description,
                suggested_queries: angleGap.suggested_queries,
              },
            },
          }}
          onOpenChange={(o) => {
            if (!o) setAngleGap(null);
          }}
          onDone={(id) => {
            setAngleGap(null);
            navigate({
              to: "/concepts/$conceptId/curate",
              params: { conceptId },
              search: { angle: id },
            });
          }}
        />
      )}
    </div>
  );
}
